/** @format */

import {
  CEOPS_CLIENT_TIMEOUT_MS,
  CEOPS_MIN_TIMEOUT_MS,
  CeopsResponse,
  CeopsScript,
} from '../constants/ceops.constant';
import { Injectable, TransferState, inject, makeStateKey } from '@angular/core';
import { Observable, TimeoutError, defer, forkJoin, of } from 'rxjs';
import { catchError, map, retry, tap, timeout } from 'rxjs/operators';

import { Environment } from '@shared/data-access/interfaces';
import { HttpClient } from '@angular/common/http';
import { TracerService } from '@shared/ssr-core/data-access';

type CeopsOutcome = 'success' | 'timeout' | 'error' | 'cached';

@Injectable({
  providedIn: 'root',
})
export class CeopsService {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(Environment);
  private readonly state = inject(TransferState);
  private readonly tracer = inject(TracerService);

  readonly CEOPS_SCRIPS_STATE_KEY_PREFIX = 'ceops-scripts';
  readonly CEOPS_URL: string = this.environment.api.ceops;
  readonly ECOMM_SCRIPT_API_URL: string = this.environment.api.ecommScripts;

  public getScripts$(
    urlToCheck: string,
    chain: string,
    retries = 0,
    useCachedCeopsScripts = false,
    isEcommScriptsKillSwitch = false,
    timeoutMs: number = CEOPS_CLIENT_TIMEOUT_MS,
  ): Observable<CeopsResponse | null> {
    const ceopsAPI = `${this.CEOPS_URL}/out`;
    const requestCEOPS = `${this.CEOPS_URL}/scripts?chain=${chain}&page=${urlToCheck}&all=true`;
    const ecommScriptAPI = this.ECOMM_SCRIPT_API_URL;
    const requestEcommScriptUrl = `${this.ECOMM_SCRIPT_API_URL}/index.json`;

    const STATE_KEY = makeStateKey<CeopsResponse>(this.CEOPS_SCRIPS_STATE_KEY_PREFIX);
    const effectiveTimeoutMs = Math.max(timeoutMs, CEOPS_MIN_TIMEOUT_MS);

    return defer(() => {
      const startTime = Date.now();

      if (useCachedCeopsScripts) {
        const stateData = this.state.get(STATE_KEY, null);
        if (stateData) {
          this.logOutcome('cached', startTime, isEcommScriptsKillSwitch, useCachedCeopsScripts);
          return of(stateData);
        }
      }

      const request: { [key: string]: Observable<CeopsResponse | null> } = {
        CEOPS: this.http.get<CeopsResponse>(requestCEOPS),
      };

      if (isEcommScriptsKillSwitch) {
        request.ECOMM_SCRIPTS = this.http.get<CeopsResponse>(requestEcommScriptUrl);
      }

      return forkJoin(request).pipe(
        map(({ CEOPS, ECOMM_SCRIPTS }) => {
          const scriptsMerged = this.mergeScriptsWithOverride(CEOPS, ECOMM_SCRIPTS);
          const response = isEcommScriptsKillSwitch ? scriptsMerged : CEOPS;

          if (!response?.scripts?.length) return response;

          response.scripts = this.joinScripts(response, ecommScriptAPI, ceopsAPI);

          if (isEcommScriptsKillSwitch) {
            response.scripts = this.applyFilters(response.scripts, urlToCheck, chain, false, true);
          }

          return response;
        }),
        tap((response) => {
          if (useCachedCeopsScripts) this.state.set(STATE_KEY, response);
        }),
        timeout(effectiveTimeoutMs),
        retry(retries),
        tap(() => {
          this.logOutcome('success', startTime, isEcommScriptsKillSwitch, useCachedCeopsScripts);
        }),
        catchError((error) => {
          this.logOutcome(
            error instanceof TimeoutError ? 'timeout' : 'error',
            startTime,
            isEcommScriptsKillSwitch,
            useCachedCeopsScripts,
          );

          const fallback: CeopsResponse = {
            data_source: '',
            scripts: [],
            source: '',
            version: '0',
          };
          return of(fallback);
        }),
      );
    });
  }

  private logOutcome(
    outcome: CeopsOutcome,
    startTime: number,
    ecommScriptsEnabled: boolean,
    cachedEnabled: boolean,
  ): void {
    this.tracer.log('ceops_scripts_fetch_completed', {
      'ceops.duration_ms': Date.now() - startTime,
      'ceops.outcome': outcome,
      'ceops.ecomm_scripts_enabled': ecommScriptsEnabled,
      'ceops.cached_enabled': cachedEnabled,
    });
  }

  private applyFilters(
    list: CeopsScript[],
    page: string,
    chain: string,
    sync: boolean,
    allScripts: boolean,
  ): CeopsScript[] {
    if (!list) return [];

    const normalizedChain = chain.toUpperCase();

    return list.filter((item) => {
      if (!item.active) return false;

      if (item.chains?.length && !item.chains.some((c) => c.toUpperCase() === normalizedChain)) {
        return false;
      }

      if (item.placements?.length && !this.checkPageInArray(page, item.placements)) {
        return false;
      }

      if (item.exclusions?.length && this.checkPageInArray(page, item.exclusions)) {
        return false;
      }

      if (!allScripts) {
        if (sync && !item.loadSynchronously) return false;
        if (!sync && item.loadSynchronously) return false;
      }

      return true;
    });
  }

  private checkPageInArray(page: string, array: string[]): boolean {
    page = page.toLowerCase();
    const rootPage = page.includes('?') ? page.slice(0, page.indexOf('?')) : page;

    for (const index in array) {
      if (
        page.includes(array[index].toLowerCase()) ||
        (array[index] === 'root' &&
          (page === '/' ||
            rootPage.substr(rootPage.length - 5) === '.com/' ||
            rootPage.substr(rootPage.length - 4) === '.com'))
      )
        return true;
    }
    return false;
  }

  private mergeScriptsWithOverride(
    baseResponse: CeopsResponse | null | undefined,
    overrideResponse: CeopsResponse | null | undefined,
  ): CeopsResponse {
    const baseScripts = baseResponse?.scripts ?? [];
    const overrideScripts = overrideResponse?.scripts ?? [];
    const scriptsByPath = new Map<string, CeopsScript>();

    for (const script of baseScripts) {
      const key = (script.path || '').toLowerCase();
      scriptsByPath.set(key, script);
    }

    for (const script of overrideScripts) {
      const key = (script.path || '').toLowerCase();
      scriptsByPath.set(key, script);
    }

    return {
      data_source: overrideResponse?.data_source ?? baseResponse?.data_source ?? '',
      source: overrideResponse?.source ?? baseResponse?.source ?? '',
      version: overrideResponse?.version ?? baseResponse?.version ?? '0',
      scripts: Array.from(scriptsByPath.values()),
    };
  }

  public joinScripts(response: CeopsResponse, ecommScriptAPI: string, ceopsAPI: string) {
    const { scripts, version } = response;
    const versionParam = `?_=${version}`;
    const ecommApi = ecommScriptAPI;
    const ceopsApi = ceopsAPI;

    for (const element of scripts) {
      const script = element;

      if (!script?.path) continue;
      const { path, ecommScript } = script;

      if (path.includes('//')) {
        if (script.src !== path) {
          script.src = path;
        }
        continue;
      }

      const baseUrl = ecommScript ? ecommApi : ceopsApi;

      script.src = `${baseUrl}${path}.min.js${versionParam}`;
    }

    return scripts;
  }
}
